// src/components/CanvasArea.jsx
import React, { useEffect, useRef, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import FitScreenIcon from "@mui/icons-material/FitScreen";

import LineList from "./LineList";
import ResponsiveOrderContainer from "./ResponsiveOrderContainer";
import { useData } from "../context/DataContext";
import { useCanvas } from "../context/CanvasContext";
import { Image, Pattern } from "fabric";

export default function CanvasArea() {
  const {
    lines,
    addLine,
    selectedProduct,
    backgroundImage,
    lockBackgroundImage,
    line,
  } = useData();

  const containerRef = useRef(null);
  const canvasElRef = useRef(null);
  const patternRef = useRef(null);

  const { canvas, initCanvas } = useCanvas();

  // INIT CANVAS
  useEffect(() => {
    if (!canvasElRef.current) return;
    initCanvas(canvasElRef.current);
  }, [initCanvas]);

  // AUTO-ADD FIRST LINE
  useEffect(() => {
    if (lines.length === 0 && selectedProduct) {
      addLine();
    }
  }, [lines.length, selectedProduct, addLine]);

  useEffect(() => {
    if (!canvas) return;
    canvas.backgroundVpt = false;

    if (!patternRef.current) {
      const size = 20;
      const pat = document.createElement("canvas");
      pat.width = size;
      pat.height = size;
      const ctx = pat.getContext("2d");
      if (!ctx) return;

      ctx.fillStyle = "#f5f5f5";
      ctx.fillRect(0, 0, size, size);
      ctx.fillStyle = "#e0e0e0";
      ctx.fillRect(0, 0, 10, 10);
      ctx.fillRect(10, 10, 10, 10);

      patternRef.current = new Pattern({
        source: pat,
        repeat: "repeat",
      });
    }

    canvas.backgroundColor = patternRef.current;
    canvas.requestRenderAll();
  }, [canvas]);

  // RESIZE CANVAS
  useEffect(() => {
    if (!canvas || !containerRef.current) return;

    const resize = () => {
      const rect = containerRef.current.getBoundingClientRect();
      const width = Math.floor(rect.width * 0.98);
      const height = Math.floor(rect.height * 0.98);

      canvas.setWidth(width);
      canvas.setHeight(height);
      canvas.setDimensions({ width, height }, { cssOnly: true });
      canvas.renderAll();
    };

    resize();
    window.addEventListener("resize", resize);
    const observer = new ResizeObserver(resize);
    observer.observe(containerRef.current);

    return () => {
      window.removeEventListener("resize", resize);
      observer.disconnect();
    };
  }, [canvas]);

  // BACKGROUND IMAGE
  useEffect(() => {
    if (!canvas) return;

    let bgImg = canvas.getObjects().find((obj) => obj.isBackground);

    // REMOVE OLD BACKGROUND
    if (backgroundImage === null) {
      if (bgImg) canvas.remove(bgImg);
      canvas.renderAll();
      return;
    }

    // CREATE NEW BACKGROUND
    if (!bgImg) {
      const imgElement = new window.Image();
      imgElement.crossOrigin = "anonymous";
      imgElement.src = backgroundImage;

      imgElement.onload = () => {
        bgImg = new Image(imgElement, {
          isBackground: true,
          selectable: !lockBackgroundImage,
          evented: !lockBackgroundImage,
          hasControls: !lockBackgroundImage,
        });

        const scale = Math.min(
          canvas.width / bgImg.width,
          canvas.height / bgImg.height,
        );

        bgImg.scale(scale);

        bgImg.set({
          left: (canvas.width - bgImg.getScaledWidth()) / 2,
          top: (canvas.height - bgImg.getScaledHeight()) / 2,
        });
        bgImg.setCoords();

        canvas.add(bgImg);
        canvas.sendObjectToBack(bgImg);
        canvas.renderAll();
      };
      return;
    }

    bgImg.set({
      selectable: !lockBackgroundImage,
      evented: !lockBackgroundImage,
      hasControls: !lockBackgroundImage,
    });

    canvas.renderAll();
  }, [backgroundImage, lockBackgroundImage]);

  // ZOOM LEVEL TRACKING — safe
  const [zoom, setZoom] = useState(1);

  useEffect(() => {
    if (!canvas) return;

    const updateZoom = () => setZoom(canvas.getZoom() || 1);

    updateZoom();
    canvas.on("after:render", updateZoom);
    canvas.on("mouse:wheel", updateZoom);

    return () => {
      canvas.off("after:render", updateZoom);
      canvas.off("mouse:wheel", updateZoom);
    };
  }, [canvas]);

  // SHOW ZOOM CONTROLS ONLY WHEN NEEDED — 100% CRASH-PROOF
  const [showZoomControls, setShowZoomControls] = useState(false);

  useEffect(() => {
    if (!canvas) {
      setShowZoomControls(false);
      return;
    }

    const checkIfZoomNeeded = () => {
      try {
        const textObjects = canvas
          .getObjects("i-text")
          .filter((o) => o.selectable);
        if (textObjects.length === 0) {
          setShowZoomControls(canvas.getZoom() !== 1);
          return;
        }

        // Get bounding rect of all text objects safely
        let left = Infinity,
          top = Infinity,
          right = -Infinity,
          bottom = -Infinity;
        let hasValidObject = false;

        textObjects.forEach((obj) => {
          if (!obj.visible || !obj.left || !obj.top) return;
          const bound = obj.getBoundingRect(true); // true = include stroke
          if (bound.width === 0 || bound.height === 0) return;

          hasValidObject = true;
          left = Math.min(left, bound.left);
          top = Math.min(top, bound.top);
          right = Math.max(right, bound.left + bound.width);
          bottom = Math.max(bottom, bound.top + bound.height);
        });

        if (!hasValidObject) {
          setShowZoomControls(canvas.getZoom() !== 1);
          return;
        }

        const contentWidth = right - left;
        const contentHeight = bottom - top;
        const canvasW = canvas.getWidth();
        const canvasH = canvas.getHeight();

        const tooWide = contentWidth > canvasW * 0.9;
        const tooTall = contentHeight > canvasH * 0.9;
        const isZoomed = Math.abs(canvas.getZoom() - 1) > 0.01;

        setShowZoomControls(tooWide || tooTall || isZoomed);
      } catch (err) {
        // Never crash — just hide controls if something goes wrong
        setShowZoomControls(canvas.getZoom() !== 1);
      }
    };

    // Check often but safely
    checkIfZoomNeeded();
    const handle = () => checkIfZoomNeeded();

    canvas.on("object:modified", handle);
    canvas.on("object:added", handle);
    canvas.on("object:removed", handle);
    canvas.on("after:render", handle);
    canvas.on("mouse:wheel", handle);

    const interval = setInterval(checkIfZoomNeeded, 500); // gentle fallback

    return () => {
      clearInterval(interval);
      canvas.off("object:modified", handle);
      canvas.off("object:added", handle);
      canvas.off("object:removed", handle);
      canvas.off("after:render", handle);
      canvas.off("mouse:wheel", handle);
    };
  }, [canvas, lines.length]);

  // Zoom functions — safe
  const zoomIn = () =>
    canvas?.zoomToPoint(
      { x: canvas.width / 2, y: canvas.height / 2 },
      (canvas.getZoom() || 1) * 1.25,
    );
  const zoomOut = () =>
    canvas?.zoomToPoint(
      { x: canvas.width / 2, y: canvas.height / 2 },
      (canvas.getZoom() || 1) * 0.8,
    );
  const zoomFit = () =>
    canvas?.zoomToPoint({ x: canvas.width / 2, y: canvas.height / 2 }, 1);

  return (
    <>
      <Box
        ref={containerRef}
        sx={{
          width: "100%",
          height: { xs: 340, sm: 400, md: 500 },
          bgcolor: "#f8f8f8",
          border: "1px solid #ddd",
          borderRadius: 2,
          overflow: "hidden",
          position: "relative",
          mb: 2,
        }}
      >
        <canvas ref={canvasElRef} />

        {showZoomControls && (
          <Box
            sx={{
              position: "absolute",
              bottom: { xs: 12, md: 16 },
              right: { xs: 12, md: 16 },
              zIndex: 10,
              bgcolor: "rgba(255, 255, 255, 0.9)",
              backdropFilter: "blur(4px)",
              border: "2px solid #FF6B35",
              borderRadius: { xs: 4, md: 3 },
              p: { xs: 1, md: 0.5 },
              boxShadow: 6,
              display: "flex",
              alignItems: "center",
              gap: { xs: 0.5, md: 0 },
            }}
          >
            <IconButton
              size="medium"
              onClick={zoomOut}
              sx={{ p: { xs: 1.5, md: 0.5 } }}
            >
              <ZoomOutIcon fontSize="small" />
            </IconButton>
            <Typography
              variant="caption"
              sx={{ mx: 1, fontWeight: "bold", userSelect: "none" }}
            >
              {Math.round(zoom * 100)}%
            </Typography>
            <IconButton
              size="medium"
              onClick={zoomFit}
              sx={{ p: { xs: 1.5, md: 0.5 } }}
            >
              <FitScreenIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="medium"
              onClick={zoomIn}
              sx={{ p: { xs: 1.5, md: 0.5 } }}
            >
              <ZoomInIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          bgcolor: "background.paper",
          p: 2,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          mt: -1,
          zIndex: 2,
        }}
      >
        <LineList />
      </Box>

      <ResponsiveOrderContainer />
    </>
  );
}
